import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { Domain } from "@app2/alert/constants";
import { AlertClientService } from "@app2/clients/alert-client.service";
import { Alert } from "@app2/clients/types/alert-typedefs";
import { AlertSelectorComponent } from "@app2/alert/alert-inbox/alert-selector.component";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { getSort } from "@app2/alert/alert-inbox/utils";
import { InboxDropdownFilterType, SortType, StateType } from "@app2/fraud/type-defs";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { DEFAULT_PAGE_SIZE } from "@app2/shared/shared-constants";
import { getMessageForError } from "@app2/util/errors/handleable-errors";

@Component({
    selector: "trigger-mode-viewer",
    template: `
        <div class="inbox-left-container">
            <header class="inbox-left-header">
                <alert-inbox-sort-dropdown [selectedSort]="sort"
                                           [state]="state">
                </alert-inbox-sort-dropdown>
            </header>
            <alert-selector [loading]="loading"
                            [alerts]="allAlerts"
                            [selectedTriggerId]="selectedTriggerId"
                            [loadingMore]="loadingMoreTriggers"
                            [canLoadMore]="canLoadMoreTriggers"
                            emptyMessageKey="NO_MORE_TRIGGERS"
                            (onAlertSelected)="onTriggerSelected.emit($event.triggerId)"
                            (onRequestMore)="loadMoreTriggers()">
            </alert-selector>
        </div>
        <div class="inbox-right-container">
            <single-trigger-display [loading]="loading"
                                    [triggerId]="selectedTriggerId"
                                    [state]="state"
                                    (onAlertsStateChanged)="onAlertsStateChanged($event)">
            </single-trigger-display>
        </div>
    `,
    host: {
        style: "display: contents;",
    },
})
export class TriggerModeViewerComponent implements OnChanges {
    @Input() dropdownFilter: InboxDropdownFilterType;
    @Input() state: StateType;
    @Input() sort: SortType;
    @Input() selectedTriggerId: uuid;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onTriggerSelected = new EventEmitter<uuid>();

    @ViewChild(AlertSelectorComponent) alertSelector: AlertSelectorComponent;

    allAlerts: Alert[] = [];
    loading: boolean;
    loadingMoreTriggers: boolean;
    canLoadMoreTriggers: boolean;

    constructor(@Inject(PRODUCT_DOMAIN) private inboxDomain: Domain,
                private readonly alertClient: AlertClientService,
                private readonly notificationsService: NotificationsService) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (((changes.state || changes.sort) && this.state && this.sort) ||
            (changes.dropdownFilter && this.dropdownFilter)) {
            this.refreshTriggerList();
        }

        if (!this.selectedTriggerId && this.allAlerts.length > 0) {
            this.selectedTriggerId = this.allAlerts[0].triggerId;
        }
    }

    onAlertsStateChanged(numAlertsRemaining: number) {
        // Select next trigger if there are no more alerts
        if (numAlertsRemaining === 0) {
            const nextAlert = this.allAlerts[this.allAlerts.map(alert => alert.triggerId).lastIndexOf(this.selectedTriggerId) + 1];
            this.onTriggerSelected.emit(nextAlert?.triggerId);
            this.refreshTriggerList();
        }
    }

    loadMoreTriggers(): Promise<any> {
        let filters;
        if (!this.dropdownFilter || this.dropdownFilter === "all") {
            filters = [`state:${this.state}`];
        } else if (this.dropdownFilter === "informational") {
            filters = [`severity:${this.dropdownFilter}`, `state:${this.state}`];
        } else {
            filters = [`managedByTrac:${this.dropdownFilter}`, `state:${this.state}`];
        }

        const sort = getSort(this.sort);
        this.loadingMoreTriggers = true;
        return this.alertClient.getAlertPageGroupedByTrigger(sort.desc, sort.field, this.inboxDomain,
            filters, this.allAlerts[this.allAlerts.length - 1])
            .then(newAlerts => {
                this.allAlerts = [...this.allAlerts, ...newAlerts];
                this.canLoadMoreTriggers = newAlerts.length === DEFAULT_PAGE_SIZE;

                if (!this.selectedTriggerId && this.allAlerts.length > 0) {
                    this.selectedTriggerId = this.allAlerts[0].triggerId;
                    this.onTriggerSelected.emit(this.selectedTriggerId);
                }
            })
            .catch(error => {
                const errorMessage = getMessageForError(error.error, "ERROR_LOADING_ALERTS");
                this.notificationsService.showError(errorMessage, error);
            })
            .finally(() => this.loadingMoreTriggers = false);
    }

    private refreshTriggerList() {
        this.loading = true;
        this.allAlerts = [];
        this.loadMoreTriggers()
            .finally(() => this.loading = false);
    }
}
